import { RcAPI, getApiBaseUrl } from '../../components/common/rcApi';
import { RC_API_EVENT_MEDIA_STATUS } from '../../components/pages/dashboard/EventMedia/eventMediaConstants';

const MAX_CONCURRENT_MEDIA_REQUESTS = 2;
const MAX_RETRIES = 3; // Maximum number of retries for media requests
const POLLING_INTERVAL = 5000; // 5 seconds

export const EVENT_FLAG_IDS = {
    EVENT_FLAG_REVIEWED: "REVIEWED",
    EVENT_FLAG_DISMISSED: "DISMISSED",
    EVENT_FLAG_COACHING_REQUIRED: "COACHING_REQUIRED",
    EVENT_FLAG_COACHING_COMPLETED: "COACHING_COMPLETED",
    EVENT_FLAG_UNDER_INVESTIGATION: "UNDER_INVESTIGATION",
    EVENT_FLAG_FALSE_POSITIVE: "FALSE_POSITIVE"
};

export const ACTION_TYPES = {
    SET_EVENT_FLAGS: 'SET_EVENT_FLAGS',
    CLEAR_EVENT_FLAGS: 'CLEAR_EVENT_FLAGS',
    SET_EVENT_NOTE: 'SET_EVENT_NOTE',
    CLEAR_EVENT_NOTE: 'CLEAR_EVENT_NOTE',
    SET_EVENT_MEDIA_REQUEST: 'SET_EVENT_MEDIA_REQUEST',

    // For Queue Management
    ADD_MEDIA_TO_QUEUE: 'ADD_MEDIA_TO_QUEUE',
    REMOVE_MEDIA_FROM_QUEUE: 'REMOVE_MEDIA_FROM_QUEUE',
    INCREMENT_ACTIVE_MEDIA_REQUESTS: 'INCREMENT_ACTIVE_MEDIA_REQUESTS',
    DECREMENT_ACTIVE_MEDIA_REQUESTS: 'DECREMENT_ACTIVE_MEDIA_REQUESTS',
};

const eventFlagsTemplate = {
    eventFlags: undefined,
    eventFlagPending: false,
    eventFlagChecked: false,
    eventFlagErrorMessage: undefined,
}

const eventNoteTemplate = {
    eventNote: undefined,
    eventNotePending: false,
    eventNoteSaved: false,
    eventNoteErrorMessage: undefined,
}

const eventMediaRequestTemplate = {
    eventMedia: undefined,
    cameraType: undefined,
    status: undefined,
    errorMessage: undefined,
}

const initialState = {
    eventFlagsById: {}, // Store flags by event Template
    eventNoteById: {}, // Store notes by event Template
    settingEventsFlagsAndNotesProgress: 0,
    eventMediaRequestsById: {}, // Store media requests by event Template

    // Queue State
    mediaRequestQueue: [],
    activeMediaRequests: 0,
};

const MediaRequestEventTypeMap = {
    EVENT_RAVEN_UNPLUGGED: 'RAVEN_UNPLUGGED',
    EVENT_CAR_BATTERY_LOW: 'CAR_BATTERY_LOW',
    EVENT_AGGRESSIVE_ACCELERATION: 'AGGRESSIVE_ACCELERATION',
    EVENT_HARD_BRAKING: 'HARD_BRAKING',
    EVENT_OBD_DTC_ALERT: 'OBD_DTC_ALERT',
    EVENT_OBD_DTC_CLEARED: 'OBD_DTC_CLEARED',
    EVENT_ENGINE_ON: 'ENGINE_ON',
    EVENT_ENGINE_OFF: 'ENGINE_OFF',
    EVENT_RAVEN_PLUGGED_IN: 'RAVEN_PLUGGED_IN',
    EVENT_RAVEN_SHUTDOWN: 'RAVEN_SHUTDOWN',
    EVENT_STORAGE_SD_CARD_INSERTED: 'STORAGE_SD_CARD_INSERTED',
    EVENT_STORAGE_SD_CARD_REMOVED: 'STORAGE_SD_CARD_REMOVED',
    EVENT_STORAGE_SD_CARD_ERROR: 'STORAGE_SD_CARD_ERROR',
    EVENT_RAVEN_BT_BUTTON_SHORT_PRESS: 'BT_BUTTON_SHORT_PRESS',
    EVENT_UPDATE_REQUEST_RECEIVED: 'UPDATE_REQUEST_RECEIVED',
    EVENT_UPDATE_PACKAGE_RECEIVED: 'UPDATE_PACKAGE_RECEIVED',
    EVENT_UPDATE_REBOOT: 'UPDATE_REBOOT',
    EVENT_UPDATE_COMPLETE: 'UPDATE_COMPLETE',
    EVENT_RAVEN_BOOTED: 'RAVEN_BOOTED',
    EVENT_WEBRTC_STREAMING_STARTED: 'WEBRTC_STREAMING_STARTED',
    EVENT_WEBRTC_STREAMING_STOPPED: 'WEBRTC_STREAMING_STOPPED',
    EVENT_RAVEN_USER_SHUTDOWN: 'RAVEN_USER_SHUTDOWN',
    EVENT_UPDATE_FAILED: 'UPDATE_FAILED',
    EVENT_CAR_BUMPED: 'CAR_BUMPED',
    EVENT_STORAGE_SD_CARD_NEEDS_FORMAT: 'STORAGE_SD_CARD_NEEDS_FORMAT',
    EVENT_SPEEDING_STARTED: 'SPEEDING_STARTED',
    EVENT_SPEEDING_STOPPED: 'SPEEDING_STOPPED',
    EVENT_STORAGE_SD_CARD_FULL: 'STORAGE_SD_CARD_FULL',
    EVENT_STORAGE_SD_CARD_TOO_SMALL: 'STORAGE_SD_CARD_TOO_SMALL',
    EVENT_MEDIA_RECORDING_FAIL: 'MEDIA_RECORDING_FAIL',
    EVENT_RAVEN_MQTT_DISCONNECTED: 'RAVEN_MQTT_DISCONNECTED',
    EVENT_RAVEN_MQTT_CONNECTED: 'RAVEN_MQTT_CONNECTED',
    EVENT_RAVEN_GEOFENCE_ENTER: 'GEOFENCE_ENTER',
    EVENT_RAVEN_GEOFENCE_EXIT: 'GEOFENCE_EXIT',
    EVENT_IDLING_STARTED: 'IDLING_STARTED',
    EVENT_IDLING_STOPPED: 'IDLING_STOPPED',
    EVENT_KLOUD_DRIVER_DETECTED: 'DRIVER_DETECTED',
    EVENT_KLOUD_LIVE_STREAMING: 'LIVE_STREAMING',
    EVENT_POSSIBLE_IMPACT: 'POSSIBLE_IMPACT',
    EVENT_STORAGE_SD_CARD_MOUNTED: 'STORAGE_SD_CARD_MOUNTED',
    EVENT_STORAGE_SD_CARD_UNMOUNTED: 'STORAGE_SD_CARD_UNMOUNTED',
    EVENT_STORAGE_SD_CARD_NOT_INSERTED: 'STORAGE_SD_CARD_NOT_INSERTED',
    EVENT_STORAGE_SD_CARD_FORMAT_STARTED: 'STORAGE_SD_CARD_FORMAT_STARTED',
    EVENT_KLOUD_HISTORICAL_STREAMING: 'HISTORICAL_STREAMING',
    EVENT_RAVEN_VISION_TAILGATING: 'TAILGATING',
    EVENT_CUSTOM: 'CUSTOM',
    EVENT_HARD_CORNERING: 'EVENT_HARD_CORNERING',
    EVENT_RAVEN_CELLPHONE_DETECTED: 'CELLPHONE_DETECTED',
    EVENT_RAVEN_CAMERA_OBSCURED: 'CAMERA_OBSCURED',
    EVENT_RAVEN_SWERVING_DETECTED: 'SWERVING_DETECTED',
    EVENT_RAVEN_BAD_INSTALL_DETECTED: 'BAD_INSTALL_DETECTED',
    EVENT_RAVEN_BAD_INSTALL_CLEARED: 'BAD_INSTALL_CLEARED',
    EVENT_RAVEN_SMOKING_DETECTED: 'SMOKING_DETECTED',
    EVENT_RAVEN_EATING_DETECTED: 'EATING_DETECTED',
    EVENT_RAVEN_DRINKING_DETECTED: 'DRINKING_DETECTED',
    EVENT_RAVEN_TIRED_DETECTED: 'TIRED_DETECTED',
    EVENT_RAVEN_DISTRACTED_DETECTED: 'DISTRACTED_DETECTED',
    EVENT_RAVEN_WORK_ZONE_DETECTED_ENTER: 'WORK_ZONE_DETECTED_ENTER',
    EVENT_RAVEN_WORK_ZONE_DETECTED_EXIT: 'WORK_ZONE_DETECTED_EXIT',
    EVENT_RAVEN_REFUELING: 'REFUELING',
    EVENT_UPDATE_PACKAGE_INSTALLING: 'UPDATE_PACKAGE_INSTALLING',
    EVENT_RAVEN_BT_BTN_SINGLE_PRESS: 'BT_BTN_SINGLE_PRESS',
    EVENT_RAVEN_BT_BTN_DOUBLE_PRESS: 'BT_BTN_DOUBLE_PRESS',
    EVENT_RAVEN_BT_BTN_SINGLE_PRESS_AND_HOLD: 'BT_BTN_SINGLE_PRESS_AND_HOLD',
    EVENT_RAVEN_BT_BTN_DOUBLE_PRESS_AND_HOLD: 'BT_BTN_DOUBLE_PRESS_AND_HOLD',
    EVENT_RAVEN_BT_BTN_PAIRED: 'BT_BTN_PAIRED',
    EVENT_RAVEN_BT_BTN_UNPAIRED: 'BT_BTN_UNPAIRED',
    EVENT_RAVEN_POTENTIAL_SPEED_CAMERA_VIOLATION: 'POTENTIAL_SPEED_CAMERA_VIOLATION',
    EVENT_RAVEN_RED_LIGHT_VIOLATION: 'RED_LIGHT_VIOLATION',
    EVENT_RAVEN_POTENTIAL_RED_LIGHT_CAMERA_VIOLATION: 'POTENTIAL_RED_LIGHT_CAMERA_VIOLATION',
    EVENT_RAVEN_STOP_SIGN_VIOLATION: 'STOP_SIGN_VIOLATION',
};

const promiseTimeout = (milliseconds) => {
    return new Promise( (resolve) => setTimeout(resolve, milliseconds));
}

export const ACTIONS = {
    setDefaultEventFlags: (ravenUuid, eventId, eventFlags) => {
        return {
            type: ACTION_TYPES.SET_EVENT_FLAGS,
            payload: {
                ravenUuid,
                eventId,
                eventFlags,

                eventFlagPending: true,
                eventFlagChecked: false,
                eventFlagErrorMessage: undefined,
            },
        }
    },
    updatingEventFlag: (isChecked, ravenUuid, eventId, eventFlags) => {
        return {
            type: ACTION_TYPES.SET_EVENT_FLAGS,
            payload: {
                ravenUuid,
                eventId,
                eventFlags,

                eventFlagPending: true,
                eventFlagChecked: isChecked,
                eventFlagErrorMessage: undefined,
            },
        }
    },
    setEventFlags: (isChecked, errorMessage = undefined, ravenUuid, eventId, eventFlags) => {
        return {
            type: ACTION_TYPES.SET_EVENT_FLAGS,
            payload: {
                ravenUuid,
                eventId,
                eventFlags,

                eventFlagPending: false,
                eventFlagChecked: isChecked,
                eventFlagErrorMessage: errorMessage,
            },
        }
    },
    clearEventFlags: () => ({
        type: ACTION_TYPES.CLEAR_EVENT_FLAGS,
    }),

    setDefaultEventNote: (ravenUuid, eventId, eventNote) => {
        return {
            type: ACTION_TYPES.SET_EVENT_NOTE,
            payload: {
                ravenUuid,
                eventId,
                eventNote,

                eventNotePending: true,
                eventNoteSaved: false,
                eventNoteErrorMessage: undefined,
            },
        }
    },
    updatingEventNote: (isSaved, ravenUuid, eventId, eventNote) => {
        return {
            type: ACTION_TYPES.SET_EVENT_NOTE,
            payload: {
                ravenUuid,
                eventId,
                eventNote,

                eventNotePending: true,
                eventNoteSaved: isSaved,
                eventNoteErrorMessage: undefined,
            },
        }
    },
    setEventNote: (isSaved, errorMessage = undefined, ravenUuid, eventId, eventNote) => {
        return {
            type: ACTION_TYPES.SET_EVENT_NOTE,
            payload: {
                ravenUuid,
                eventId,
                eventNote,

                eventNotePending: false,
                eventNoteSaved: isSaved,
                eventNoteErrorMessage: errorMessage,
            },
        }
    },
    clearEventNote: () => ({
        type: ACTION_TYPES.CLEAR_EVENT_NOTE,
    }),

    setEventMediaRequest: (ravenUuid, eventId, cameraType, status, errorMessage = undefined) => {
        return {
            type: ACTION_TYPES.SET_EVENT_MEDIA_REQUEST,
            payload: {
                ravenUuid,
                eventId,
                cameraType,
                status,
                errorMessage,
            },
        }
    },

    saveEventMedia: (ravenUuid, eventId, cameraType, content) => {
        return {
            type: ACTION_TYPES.SET_EVENT_MEDIA_REQUEST,
            payload: {
                ravenUuid,
                eventId,
                cameraType,
                status: RC_API_EVENT_MEDIA_STATUS.AVAILABLE,
                eventMedia: content,
                errorMessage: undefined, // Reset error message on success
            },
        }
    },

    // Queue Management Actions
    addMediaToQueue: (request) => ({
        type: ACTION_TYPES.ADD_MEDIA_TO_QUEUE,
        payload: request
    }),
    removeMediaFromQueue: (requestId) => ({
        type: ACTION_TYPES.REMOVE_MEDIA_FROM_QUEUE,
        payload: requestId
    }),
    incrementActiveMediaRequests: () => ({
        type: ACTION_TYPES.INCREMENT_ACTIVE_MEDIA_REQUESTS
    }),
    decrementActiveMediaRequests: () => ({
        type: ACTION_TYPES.DECREMENT_ACTIVE_MEDIA_REQUESTS
    }),
};

export const DISPATCH_HELPERS = {
    
    // This is the main entry point for initiating a media request.
    initiateMediaRequest: (stage, ravenUuid, eventId, cameraType, timestamp, duration, eventType, abortController) => (dispatch, getState) => {
        const requestId = `${ravenUuid}/${eventId}/${cameraType}`;
        const state = getState().data.eventReview;

        const isInQueue = state.mediaRequestQueue.some(req => req.requestId === requestId);
        
        if (isInQueue) {
            return;
        }

        const isAlreadyProcessing = state.eventMediaRequestsById[requestId];
        
        // If the status is one of these, we can retry.
        // Anything else means it's already being processed or has failed in a way that we don't retry.
        const statusesToRetry = [
            RC_API_EVENT_MEDIA_STATUS.ERROR_REQUEST_TIMEOUT,
            RC_API_EVENT_MEDIA_STATUS.UPLOAD_FAILED,
        ];

        if (isAlreadyProcessing && !statusesToRetry.includes(isAlreadyProcessing.status)) {
            return;
        }

        const request = {
            requestId,
            stage,
            ravenUuid,
            eventId,
            cameraType,
            timestamp,
            duration,
            eventType,
            abortController, // Pass the abort controller to allow cancellation
        };

        // Dispatch actions to update the state
        dispatch(ACTIONS.setEventMediaRequest(ravenUuid, eventId, cameraType, RC_API_EVENT_MEDIA_STATUS.PENDING));
        dispatch(ACTIONS.addMediaToQueue(request));

        // Kick off the queue processor
        dispatch(DISPATCH_HELPERS.processMediaQueue());
    },

    // This is our queue processor. It checks if it can run a new request.
    processMediaQueue: () => (dispatch, getState) => {
        const state = getState().data.eventReview;
        const queue = state.mediaRequestQueue;
        const active = state.activeMediaRequests;

        if (active < MAX_CONCURRENT_MEDIA_REQUESTS && queue.length > 0) {
            const requestToProcess = queue[0];

            // Dispatch actions to update state before starting the request
            dispatch(ACTIONS.removeMediaFromQueue(requestToProcess.requestId));
            dispatch(ACTIONS.incrementActiveMediaRequests());

            // Dispatch the thunk that handles the entire lifecycle for this one request.
            dispatch(DISPATCH_HELPERS._executeMediaRequestLifecycle(requestToProcess))
                .finally(() => {
                    // This runs whether the request succeeded or failed.
                    dispatch(ACTIONS.decrementActiveMediaRequests());
                    // After finishing one, immediately check if we can start another.
                    dispatch(DISPATCH_HELPERS.processMediaQueue());
                });
        }
    },

    // This "private" thunk manages the full lifecycle for a single media request.
    _executeMediaRequestLifecycle: (request) => (dispatch) => {
        const { stage, ravenUuid, eventId, cameraType, timestamp, duration, eventType, abortController } = request;

        // Helper to dispatch failure actions consistently.
        const dispatchFailure = (error, statusMessage) => {
            
            if(error.name === "AbortError"){
                // If the request was aborted, we can just log it and not dispatch an error.
                console.warn(`Media request aborted`, error, request);
                return;
            }

            console.error(`Media request failed`, error, request);
            
            switch (error.code) {
                case 504: // Gateway Timeout
                case 408: // Request Timeout
                    dispatch(ACTIONS.setEventMediaRequest(ravenUuid, eventId, cameraType, RC_API_EVENT_MEDIA_STATUS.ERROR_REQUEST_TIMEOUT, error));
                    break;
                // case 422: // Video unavailable
                    // dispatch(ACTIONS.setEventMediaRequest(ravenUuid, eventId, cameraType, RC_API_EVENT_MEDIA_STATUS.SOURCE_GONE, error));
                    // break;
                default:
                    dispatch(ACTIONS.setEventMediaRequest(ravenUuid, eventId, cameraType, RC_API_EVENT_MEDIA_STATUS.ERROR, error));
            }
            
            return;
        };

        // 1. make the initial request
        dispatch(ACTIONS.setEventMediaRequest(ravenUuid, eventId, cameraType, RC_API_EVENT_MEDIA_STATUS.PENDING));

        return EventReviewRcAPI.requestMedia(stage, ravenUuid, cameraType, timestamp, duration, eventType, abortController)
            .then(requestMediaResponse => {

                // 2. start polling
                dispatch(ACTIONS.setEventMediaRequest(ravenUuid, eventId, cameraType, RC_API_EVENT_MEDIA_STATUS.CREATE_PREVIEW_VIDEO_REQUEST_SENT));

                const pollUpdateCallback = (body) => {
                    dispatch(ACTIONS.setEventMediaRequest(ravenUuid, eventId, cameraType, body.mediaStatus));
                };

                return EventReviewRcAPI.pollMediaStatus(stage, ravenUuid, requestMediaResponse.mediaId, pollUpdateCallback, abortController);
            })
            .then(pollMediaResponse => {
                // 3. Get the final media content URI
                return EventReviewRcAPI.getMedia(stage, ravenUuid, pollMediaResponse.camera, pollMediaResponse.timestamp, pollMediaResponse.mediaId, abortController);
            })
            .then(mediaContentUri => {
                // 4. Success! Save the media content.
                dispatch(ACTIONS.saveEventMedia(ravenUuid, eventId, cameraType, mediaContentUri));
            })
            .catch(error => {
                if(!error.code) {
                    // If the error does not have a code, it's likely a network error.
                    error.code = 504; // Default to gateway timeout
                }

                return dispatchFailure(error, "Failed to retrieve media");
            });
    }
}

export const eventReviewReducer = (state = initialState, action) => {
    switch (action.type) {
        case ACTION_TYPES.SET_EVENT_FLAGS:
            return {
                ...state,
                settingEventsFlagsAndNotesProgress: action.payload.eventFlagPending === false ? state.settingEventsFlagsAndNotesProgress + 1 : state.settingEventsFlagsAndNotesProgress,
                eventFlagsById: {
                    ...state.eventFlagsById,
                    [`${action.payload.ravenUuid}/${action.payload.eventId}`]: {
                        ...eventFlagsTemplate,
                        eventFlags: action.payload.eventFlags,
                        eventFlagPending: action.payload.eventFlagPending,
                        eventFlagChecked: action.payload.eventFlagChecked,
                        eventFlagErrorMessage: action.payload.eventFlagErrorMessage,
                    },
                },
            };

        case ACTION_TYPES.CLEAR_EVENT_FLAGS:
            return {
                ...state,
                settingEventsFlagsAndNotesProgress: 0,
                eventFlagsById: {},
            };

        case ACTION_TYPES.SET_EVENT_NOTE:
            return {
                ...state,
                eventNoteById: {
                    ...state.eventNoteById,
                    [`${action.payload.ravenUuid}/${action.payload.eventId}`]: {
                        ...eventNoteTemplate,
                        eventNote: action.payload.eventNote,
                        eventNotePending: action.payload.eventNotePending,
                        eventNoteSaved: action.payload.eventNoteSaved,
                        eventNoteErrorMessage: action.payload.eventNoteErrorMessage,
                    },
                },
            };
        
        case ACTION_TYPES.CLEAR_EVENT_NOTE:
            return {
                ...state,
                eventNoteById: {},
            };

        case ACTION_TYPES.SET_EVENT_MEDIA_REQUEST:
            const requestId = `${action.payload.ravenUuid}/${action.payload.eventId}/${action.payload.cameraType}`;
            const request = state.eventMediaRequestsById[requestId];
            return {
                ...state,
                eventMediaRequestsById: {
                    ...state.eventMediaRequestsById,
                    [requestId]: {
                        ...eventMediaRequestTemplate,
                        eventMedia: action.payload.eventMedia ? action.payload.eventMedia : request ? request.eventMedia : undefined,
                        cameraType: action.payload.cameraType ? action.payload.cameraType : request ? request.cameraType : undefined,
                        status: action.payload.status ? action.payload.status : request ? request.status : undefined,
                        errorMessage: action.payload.errorMessage ? action.payload.errorMessage : request ? request.errorMessage : undefined,
                    },
                },
            };

        // Queue Management Reducers
        case ACTION_TYPES.ADD_MEDIA_TO_QUEUE:
            return {
                ...state,
                mediaRequestQueue: [...state.mediaRequestQueue, action.payload],
            };
        
        case ACTION_TYPES.REMOVE_MEDIA_FROM_QUEUE:
            // Note: we need a unique requestId for this to work correctly.
            return {
                ...state,
                mediaRequestQueue: state.mediaRequestQueue.filter(req => req.requestId !== action.payload),
            };
        
        case ACTION_TYPES.INCREMENT_ACTIVE_MEDIA_REQUESTS:
            return {
                ...state,
                activeMediaRequests: state.activeMediaRequests + 1,
            };
        
        case ACTION_TYPES.DECREMENT_ACTIVE_MEDIA_REQUESTS:
            return {
                ...state,
                activeMediaRequests: state.activeMediaRequests - 1,
            };

        default:
            return state;
    }
};

export class EventReviewRcAPI {

    static getSingleEvent = (stage, ravenId, eventId, abortController) => {
        if (!stage) return Promise.reject(new Error('stage is required'));
        if (!ravenId) return Promise.reject(new Error('ravenId is required'));
        if (!eventId) return Promise.reject(new Error('eventId is required'));

        const apiBaseUrl = getApiBaseUrl('/user-v1');
        const apiFullUrl = `${apiBaseUrl}/ravens/${ravenId}/events/${eventId}`;
        const method = 'GET';

        return RcAPI.apiRequest(apiFullUrl, method, undefined, stage, abortController?.signal)
                    .catch((error) => {
                        console.error("EventReviewRcAPI getSingleEvent error", error);
                        throw error;
                    });
    }

    static updateEventNote = (stage, ravenId, eventId, note, abortController) => {
        if (!stage) return Promise.reject(new Error('stage is required'));
        if (!ravenId) return Promise.reject(new Error('ravenId is required'));
        if (!eventId) return Promise.reject(new Error('eventId is required'));
        if (!note) return Promise.reject(new Error('note is required'));

        const apiBaseUrl = getApiBaseUrl('/user-v1');
        const apiFullUrl = `${apiBaseUrl}/ravens/${ravenId}/events/${eventId}/note`;
        const method = 'PUT';
        const payload = {
            note: note
        };

        return RcAPI.apiRequest(apiFullUrl, method, payload, stage, abortController?.signal)
                    .catch((error) => {
                        console.error("EventReviewRcAPI updateEventNote error", error);
                        throw error;
                    });
    }

    static deleteEventNote = (stage, ravenId, eventId, abortController) => {
        if (!stage) return Promise.reject(new Error('stage is required'));
        if (!ravenId) return Promise.reject(new Error('ravenId is required'));
        if (!eventId) return Promise.reject(new Error('eventId is required'));

        const apiBaseUrl = getApiBaseUrl('/user-v1');
        const apiFullUrl = `${apiBaseUrl}/ravens/${ravenId}/events/${eventId}/note`;
        const method = 'DELETE';

        return RcAPI.apiRequest(apiFullUrl, method, undefined, stage, abortController?.signal)
                    .catch((error) => {
                        console.error("EventReviewRcAPI deleteEventNote error", error);
                        throw error;
                    });
    }

    static putEventFlag = (stage, ravenId, eventId, flagId, abortController) => {
        if (!stage) return Promise.reject(new Error('stage is required'));
        if (!ravenId) return Promise.reject(new Error('ravenId is required'));
        if (!eventId) return Promise.reject(new Error('eventId is required'));
        if (!flagId) return Promise.reject(new Error('flagId is required'));

        const apiBaseUrl = getApiBaseUrl('/user-v1');
        const apiFullUrl = `${apiBaseUrl}/ravens/${ravenId}/events/${eventId}/flag/${flagId}`;
        const method = 'PUT';

        return RcAPI.apiRequest(apiFullUrl, method, undefined, stage, abortController?.signal)
                    .catch((error) => {
                        console.error("EventReviewRcAPI putEventFlag error", error);
                        throw error;
                    });
    }

    static deleteEventFlag = (stage, ravenId, eventId, flagId, abortController) => {
        if (!stage) return Promise.reject(new Error('stage is required'));
        if (!ravenId) return Promise.reject(new Error('ravenId is required'));
        if (!eventId) return Promise.reject(new Error('eventId is required'));
        if (!flagId) return Promise.reject(new Error('flagId is required'));

        const apiBaseUrl = getApiBaseUrl('/user-v1');
        const apiFullUrl = `${apiBaseUrl}/ravens/${ravenId}/events/${eventId}/flag/${flagId}`;
        const method = 'DELETE';

        return RcAPI.apiRequest(apiFullUrl, method, undefined, stage, abortController?.signal)
                    .catch((error) => {
                        console.log("EventReviewRcAPI deleteEventFlag error", error);
                        throw error;
                    });
    }

    static requestMedia = (stage, ravenUuid, cameraType, timestamp, duration, eventType, abortController) => {

        let eventTypeAsExpectedByAPI = eventType;
        if(eventTypeAsExpectedByAPI && MediaRequestEventTypeMap[eventTypeAsExpectedByAPI]) {
            eventTypeAsExpectedByAPI = MediaRequestEventTypeMap[eventTypeAsExpectedByAPI];
        }

        const requestMediaWithBackoff = (attempt = 0) => {

            return RcAPI.requestBasicCreateRavenMedia(stage, ravenUuid, cameraType, timestamp, duration, eventTypeAsExpectedByAPI, abortController)
                .catch(error => {
                    if (!error.code) { // errors that do not have a code are likely network errors (i.e. "failed to fetch")
                            // Network errors are all "failed to fetch" because "the error description ... string is not guaranteed to remain backwards compatible between releases. You must not parse and act based upon its content."
                            // Chrome docs https://developer.chrome.com/docs/extensions/reference/api/webRequest#event-onErrorOccurred
                            // Docs are different for each browser, but the error message is not guaranteed to be consistent
                        
                        error.code = 504; // default to gateway timeout because a network error is likely being caused by an API Gateway timeout (30 seconds) and we have an exponetial backoff delay to avoid infinite loops
                    }

                    if (attempt >= MAX_RETRIES) {
                        throw error;
                    }

                    if(error.code !== 504) {
                        throw error;
                    }

                    const backoffDelay = Math.pow(2, attempt) * 1000;

                    return promiseTimeout(backoffDelay)
                            .then(() => requestMediaWithBackoff(attempt + 1));
                });
        };

        return requestMediaWithBackoff()
            .catch(error => {
                // If getApiJWT fails, or if all retries fail
                console.error("EventReviewRcAPI requestMedia failed.", error);
                error.cameraType = cameraType;
                throw error;
            });
    }

    static getMedia = (stage, ravenUuid, cameraType, timestamp, mediaId, abortController) => {

        return RcAPI.getRavenMedia(stage, ravenUuid, cameraType, timestamp, abortController)
            .then(body => {
                const mediaContentUri = body.results[0].results.find(media => media.mediaId === mediaId).contentUri;
                return mediaContentUri;
            })
            .catch(error => {
                console.error("EventReviewRcAPI getMedia failed.", error);
                error.cameraType = cameraType;
                throw error;
            });
    }

    static pollMediaStatus = (stage, ravenUuid, mediaId, updateCallback, abortController) => {

        if(!mediaId) {
            return Promise.reject(new Error('EventReviewRcAPI mediaId is required for polling media status'));
        }

        const poll = () => {

            const start = Date.now();

            return RcAPI.checkRavenMediaStatus(stage, ravenUuid, mediaId, abortController)
                .then(body => {

                    // If the media is available,
                    if (body) {

                        if(body.mediaStatus === RC_API_EVENT_MEDIA_STATUS.AVAILABLE){
                            return body;
                        }

                        if(body.mediaStatus === RC_API_EVENT_MEDIA_STATUS.ERROR) {
                            return Promise.reject(new Error('Media status is ERROR'));
                        }
                    }

                    // If it's not available, we call the update callback.
                    if (typeof updateCallback === 'function') {
                        updateCallback(body);
                    }

                    const duration = Date.now() - start;
                    const delay = Math.max(0, POLLING_INTERVAL - duration);

                    return promiseTimeout(delay)
                        .then(() => poll());
                });
        };

        return poll()
            .catch(error => {
                // either from getting the JWT or from the polling attempts.
                console.error("EventReviewRcAPI pollMediaStatus failed.", error);
                error.mediaId = mediaId; 
                throw error;
            });
    }
}